class GiftNoteDrawer extends HTMLElement {
  constructor() {
    super();
    this.handleExternalOpen = this.handleExternalOpen.bind(this);
    this.selectedOptions = {};
    this.previousScrollY = null;
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-gift-note-overlay]');
    this.panel = this.querySelector('[data-gift-note-panel]');
    this.closeButtons = this.querySelectorAll('[data-gift-note-close]');
    this.addButton = this.querySelector('[data-gift-note-add]');
    this.priceDisplay = this.querySelector('[data-gift-note-price-display]');
    this.errorEl = this.querySelector('[data-gift-note-error]');
    this.messageInput = this.querySelector('[data-gift-note-message-input]');
    this.messageCounter = this.querySelector('[data-gift-note-message-count]');
    this.mediaWrapper = this.querySelector('[data-gift-note-media]');
    this.productTitleEl = this.querySelector('[data-gift-note-title]');
    this.variantData = this.parseVariantData();
    this.stitcherData = this.parseStitcherData();
    this.optionLabelOverrides = this.buildOptionLabelOverrides(this.stitcherData);
    this.variantMediaData = this.parseVariantMediaData();
    const stitcherMediaMap = this.buildVariantMediaMap(this.stitcherData);
    const variantMediaMap = this.buildVariantMediaMap(this.variantMediaData);
    this.variantMediaMap = { ...variantMediaMap, ...stitcherMediaMap };
    this.variantTitleMap = this.buildVariantTitleMap(this.stitcherData);
    this.defaultMediaHtml = this.mediaWrapper?.innerHTML || '';
    this.defaultProductTitle = this.productTitleEl?.textContent?.trim() || '';
    this.cartDrawer = document.querySelector('cart-drawer');
    this.optionInputs = Array.from(this.querySelectorAll('.gift-note-drawer__option-values input[type="radio"]'));
    this.optionValueLabels = Array.from(this.querySelectorAll('.gift-note-drawer__option-values label'));
    this.optionLabels = Array.from(this.querySelectorAll('[data-gift-note-option-label]'));
    this.ctaText = this.querySelector('[data-gift-note-cta-text]');
    this.cartContext = null;
    this.maxMessageLines = 6;
    this.lastValidMessageValue = '';

    this.defaultVariantId = Number(this.dataset.defaultVariantId);
    this.selectedVariant =
      this.variantData.find((variant) => variant.id === this.defaultVariantId) || this.variantData[0];

    if (this.selectedVariant) {
      this.selectedVariant.options.forEach((value, index) => {
        this.selectedOptions[index + 1] = value;
      });
    }

    this.attachEvents();
    this.updateOptionInputs();
    this.applyOptionValueOverrides();
    this.updateOptionLabels();
    this.updateDisplays();
    this.updateMedia();
    this.updateProductTitle();
    this.syncMessageValue(this.messageInput?.value || '');
    this.updateMessageCounter();
  }

  disconnectedCallback() {
    document.removeEventListener('gift-note:open', this.handleExternalOpen);
  }

  attachEvents() {
    this.overlay?.addEventListener('click', () => this.close());
    this.closeButtons.forEach((btn) => btn.addEventListener('click', () => this.close()));
    this.addButton?.addEventListener('click', () => this.addToProduct());
    this.optionInputs.forEach((input) =>
      input.addEventListener('change', (event) => this.handleOptionChange(event)),
    );
    this.optionValueLabels.forEach((label) =>
      label.addEventListener('click', (event) => this.handleOptionLabelClick(event)),
    );
    this.messageInput?.addEventListener('input', () => this.handleMessageInput());

    document.addEventListener('gift-note:open', this.handleExternalOpen);
  }

  parseVariantData() {
    try {
      const jsonNode = this.querySelector('[data-gift-note-variants]');
      if (!jsonNode) return [];
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      console.warn('Unable to parse gift note variant data', error);
      return [];
    }
  }

  parseStitcherData() {
    try {
      const jsonNode = this.querySelector('[data-gift-note-stitchers]');
      if (!jsonNode) return [];
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      console.warn('Unable to parse gift note stitcher data', error);
      return [];
    }
  }

  parseVariantMediaData() {
    try {
      const jsonNode = this.querySelector('[data-gift-note-variant-media]');
      if (!jsonNode) return [];
      return JSON.parse(jsonNode.textContent);
    } catch (error) {
      console.warn('Unable to parse gift note variant media data', error);
      return [];
    }
  }

  buildOptionLabelOverrides(stitcherData = []) {
    const overrides = {};
    if (!Array.isArray(stitcherData)) return overrides;
    stitcherData.forEach((entry) => {
      if (!entry || !entry.label || !Array.isArray(entry.options)) return;
      const optionValue = entry.options[0];
      if (!optionValue) return;
      const position = 1;
      if (!overrides[position]) {
        overrides[position] = {};
      }
      if (!overrides[position][optionValue]) {
        overrides[position][optionValue] = entry.label;
      }
    });
    return overrides;
  }

  buildVariantMediaMap(stitcherData = []) {
    const mediaMap = {};
    if (!Array.isArray(stitcherData)) return mediaMap;
    stitcherData.forEach((entry) => {
      if (!entry || !entry.variantId) return;
      mediaMap[entry.variantId] = entry.image || null;
    });
    return mediaMap;
  }

  buildVariantTitleMap(stitcherData = []) {
    const titleMap = {};
    if (!Array.isArray(stitcherData)) return titleMap;
    stitcherData.forEach((entry) => {
      if (!entry || !entry.variantId) return;
      if (entry.productTitle) {
        titleMap[entry.variantId] = entry.productTitle;
      }
    });
    return titleMap;
  }

  handleExternalOpen(event) {
    const { button } = event.detail || {};
    if (button) {
      this.activeTrigger = button;
    } else {
      this.activeTrigger = document.querySelector('[data-gift-note-trigger]');
    }

    this.cartContext = event.detail?.cartContext || null;
    this.updateCtaLabel();
    this.closeCartDrawer();
    this.syncSelectionFromWidget();
    this.prefillMessageFromWidget();
    this.open();
  }

  syncSelectionFromWidget() {
    const widget = document.querySelector('[data-gift-note-widget]');
    if (!widget) return;
    const selectedId = Number(
      widget.dataset.selectedVariantId || widget.dataset.defaultVariantId || this.dataset.defaultVariantId,
    );
    const variant = this.variantData.find((item) => item.id === selectedId) || this.selectedVariant;
    if (!variant) return;
    this.selectedVariant = variant;
    this.selectedOptions = {};
    variant.options.forEach((value, index) => {
      this.selectedOptions[index + 1] = value;
    });
    this.updateOptionInputs();
    this.updateOptionLabels();
    this.updateDisplays();
    this.updateMedia();
    this.updateProductTitle();
  }

  prefillMessageFromWidget() {
    const widget = document.querySelector('[data-gift-note-widget]');
    if (!widget || !this.messageInput) return;
    const messageInput = widget.querySelector('[data-gift-note-message-input]');
    this.syncMessageValue(messageInput?.value || '');
  }

  open() {
    if (window.innerWidth <= 750) {
      this.previousScrollY = window.scrollY || 0;
    }
    document.body.classList.add('overflow-hidden');
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
  }

  close() {
    document.body.classList.remove('overflow-hidden');
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    if (this.previousScrollY !== null) {
      window.scrollTo({ top: this.previousScrollY, behavior: 'auto' });
      this.previousScrollY = null;
    }
    if (this.errorEl) this.errorEl.textContent = '';
    this.cartContext = null;
    this.updateCtaLabel();
  }

  handleOptionChange(event) {
    const input = event.currentTarget;
    if (input.disabled) return;
    const optionPosition = this.getOptionPosition(input);
    this.selectedOptions[optionPosition] = input.value;
    this.updateSelectedVariant();
  }

  handleOptionLabelClick(event) {
    const label = event.currentTarget;
    const input = this.findInputForLabel(label);
    if (!input || input.disabled) return;

    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  findInputForLabel(label) {
    if (!label) return null;
    let input = null;
    const inputId = label.getAttribute('for');

    if (inputId) {
      const escapedId =
        window.CSS && CSS.escape ? CSS.escape(inputId) : inputId.replace(/([ #;?%&,.+*~':"!^$[\]()=>|\/@])/g, '\\$1');
      input = this.querySelector(`#${escapedId}`);
    }

    if (!input) {
      const prev = label.previousElementSibling;
      if (prev?.matches?.('input[type="radio"]')) {
        input = prev;
      }
    }

    return input;
  }

  getOptionPosition(input) {
    const optionAttr = input.getAttribute('data-option');
    if (optionAttr && optionAttr.includes('option')) {
      return Number(optionAttr.replace('option', ''));
    }
    const group = input.closest('[data-option-position]');
    return Number(group?.dataset.optionPosition);
  }

  updateSelectedVariant() {
    const match = this.variantData.find((variant) =>
      variant.options.every((value, index) => this.selectedOptions[index + 1] === value),
    );
    this.selectedVariant = match || null;
    this.updateOptionInputs();
    this.updateOptionLabels();
    this.updateDisplays();
    this.updateMedia();
    this.updateProductTitle();
  }

  updateOptionInputs() {
    if (!this.optionInputs?.length) return;
    this.optionInputs.forEach((input) => {
      const optionPosition = this.getOptionPosition(input);
      const shouldCheck = this.selectedOptions[optionPosition] === input.value;
      input.checked = shouldCheck;
      input.nextElementSibling?.classList.toggle('is-active', shouldCheck);
    });
  }

  applyOptionValueOverrides() {
    if (!this.optionValueLabels?.length) return;
    this.optionValueLabels.forEach((label) => {
      const input = this.findInputForLabel(label);
      if (!input) return;
      const optionPosition = this.getOptionPosition(input);
      const override = this.getOverrideLabel(optionPosition, input.value);
      if (!override) return;
      const textNode = label.querySelector('.variant-label-text');
      if (textNode) {
        textNode.textContent = override;
      }
    });
  }

  updateOptionLabels() {
    if (!this.optionLabels?.length) return;
    this.optionLabels.forEach((label) => {
      const position = Number(label.dataset.giftNoteOptionLabel);
      const value = this.selectedOptions[position];
      if (value) {
        label.textContent = this.getOverrideLabel(position, value) || value;
      }
    });
  }

  updateMessageCounter() {
    if (this.messageInput && this.messageCounter) {
      const remaining = Math.max(0, 300 - this.messageInput.value.length);
      this.messageCounter.textContent = remaining;
    }
    this.updateCtaState();
  }

  handleMessageInput() {
    if (!this.messageInput) return;

    const limitedValue = this.limitMessageValue(this.messageInput.value);
    if (limitedValue !== this.messageInput.value) {
      this.messageInput.value = this.lastValidMessageValue || limitedValue;
    } else {
      this.lastValidMessageValue = limitedValue;
    }

    this.updateMessageCounter();
  }
  syncMessageValue(value) {
    if (!this.messageInput) return '';

    const limitedValue = this.limitMessageValue(value || '');
    this.messageInput.value = limitedValue;
    this.lastValidMessageValue = limitedValue;

    return limitedValue;
  }

  limitMessageValue(value) {
    const normalizedValue = (value || '').replace(/\r\n/g, '\n');
    if (this.isMessageValueWithinLimit(normalizedValue)) return normalizedValue;

    let limitedValue = normalizedValue;
    while (limitedValue && !this.isMessageValueWithinLimit(limitedValue)) {
      limitedValue = limitedValue.slice(0, -1);
    }

    return limitedValue;
  }

  isMessageValueWithinLimit(value) {
    if (!this.messageInput) return true;

    const normalizedValue = (value || '').replace(/\r\n/g, '\n');
    const lines = normalizedValue.split('\n');
    if (lines.length > this.maxMessageLines) return false;

    const styles = window.getComputedStyle(this.messageInput);
    const lineHeight = parseFloat(styles.lineHeight) || 0;
    const paddingTop = parseFloat(styles.paddingTop) || 0;
    const paddingBottom = parseFloat(styles.paddingBottom) || 0;
    const maxScrollHeight = lineHeight * this.maxMessageLines + paddingTop + paddingBottom;

    const previousValue = this.messageInput.value;
    this.messageInput.value = normalizedValue;
    const exceedsVisibleRows = this.messageInput.scrollHeight > maxScrollHeight + 1;
    this.messageInput.value = previousValue;

    return !exceedsVisibleRows;
  }

  updateCtaState() {
    if (!this.addButton) return;
    const hasMessage = Boolean(this.messageInput?.value.trim());
    this.addButton.disabled = !hasMessage;
    this.addButton.classList.toggle('is-disabled', !hasMessage);
  }

  addToProduct() {
    if (!this.selectedVariant) {
      this.setErrorMessage('Please select a valid option.');
      return;
    }

    if (!this.selectedVariant.available) {
      this.setErrorMessage('Selected option is unavailable.');
      return;
    }

    const messageError = this.validateMessage();
    if (messageError) {
      this.setErrorMessage(messageError);
      return;
    }

    this.setErrorMessage('');
    const finalPrice = this.selectedVariant.price;
    if (this.cartContext) {
      this.addGiftNoteToCart(finalPrice);
    } else {
      this.updateGiftWidget(finalPrice);
      this.close();
    }
  }

  validateMessage() {
    if (!this.messageInput) return null;
    this.syncMessageValue(this.messageInput.value);
    const message = this.messageInput.value.trim();
    if (!message.length) {
      return 'Please enter your gift message.';
    }
    const lines = message.split(/\r?\n/);
    if (lines.length > this.maxMessageLines) {
      return 'Please limit your message to 6 rows.';
    }
    return null;
  }

  setErrorMessage(message) {
    if (this.errorEl) {
      this.errorEl.textContent = message || '';
    }
  }

  updateGiftWidget(finalPriceCents) {
    const widget = document.querySelector('[data-gift-note-widget]');
    if (!widget) return;

    const priceEl = widget.querySelector('[data-gift-note-price]');
    const chargeInput = widget.querySelector('[data-gift-note-charge-input]');
    const idInput = widget.querySelector('[data-gift-note-id-input]');
    const messageInput = widget.querySelector('[data-gift-note-message-input]');
    const triggerBtn = this.activeTrigger || widget.querySelector('[data-gift-note-trigger]');

    if (priceEl) {
      priceEl.innerHTML = this.getPriceHtml(finalPriceCents, this.selectedVariant.price, { wrapOriginal: true });
    }

    if (chargeInput) {
      chargeInput.value = (finalPriceCents / 100).toString();
    }

    if (idInput) {
      idInput.value = this.selectedVariant.id;
    }

    if (messageInput) {
      messageInput.value = this.messageInput?.value || '';
    }

    widget.dataset.selectedVariantId = this.selectedVariant.id;
    widget.dataset.message = this.messageInput?.value || '';

    if (triggerBtn) {
      triggerBtn.setAttribute('data-price', (finalPriceCents / 100).toFixed(2));
      triggerBtn.setAttribute('data-message', this.messageInput?.value || '');
    }

    const giftWidget = document.querySelector('gift-widget');
    if (giftWidget && typeof giftWidget.addGiftProduct === 'function' && triggerBtn) {
      giftWidget.addGiftProduct(triggerBtn);
    } else if (giftWidget && triggerBtn) {
      giftWidget.handleBtn(triggerBtn);
      giftWidget.handleFields(triggerBtn);
      giftWidget.handlePrice();
    }
  }

  addGiftNoteToCart(finalPriceCents) {
    const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    if (!cartItems || !this.cartContext) return;

    const line = Number(this.cartContext.line) || 1;
    const quantity = Number(this.cartContext.quantity) || 1;
    const charges = (finalPriceCents / 100).toString();
    const surrogateTarget = {
      dataset: {
        message: this.messageInput?.value || '',
      },
    };

    cartItems.updateComponent(
      cartItems,
      line,
      quantity,
      'gift-note',
      this.selectedVariant.id,
      charges,
      surrogateTarget,
    );

    this.close();
    this.openCartDrawer();
  }

  getPriceHtml(finalPrice, originalPrice, { wrapOriginal = false } = {}) {
    if (finalPrice <= 0) {
      return wrapOriginal ? '(FREE)' : 'FREE';
    }

    if (finalPrice < originalPrice) {
      return `${this.formatCurrency(finalPrice)} <s>${this.formatCurrency(originalPrice)}</s>`;
    }

    const formatted = this.formatCurrency(originalPrice);
    return wrapOriginal ? `(${formatted})` : formatted;
  }

  formatCurrency(cents) {
    const amount = cents / 100;
    const formatted = Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2);
    const currency = this.dataset.currencySymbol || '£';
    return `${currency}${formatted}`;
  }

  updateDisplays() {
    if (!this.selectedVariant) {
      if (this.priceDisplay) this.priceDisplay.textContent = 'Unavailable';
      if (this.addButton) this.addButton.disabled = true;
      return;
    }

    const priceHtml = this.getPriceHtml(this.selectedVariant.price, this.selectedVariant.price);
    if (this.priceDisplay) {
      this.priceDisplay.innerHTML = priceHtml;
    }
    if (this.addButton) {
      this.addButton.disabled = !this.selectedVariant.available;
    }
  }

  updateMedia() {
    if (!this.mediaWrapper) return;
    const variantId = this.selectedVariant?.id;
    const mediaData = variantId ? this.variantMediaMap?.[variantId] : null;
    if (mediaData?.url) {
      this.mediaWrapper.innerHTML = this.createMediaHtml(mediaData);
    } else if (this.defaultMediaHtml) {
      this.mediaWrapper.innerHTML = this.defaultMediaHtml;
    }
  }

  updateProductTitle() {
    if (!this.productTitleEl) return;
    const variantId = this.selectedVariant?.id;
    const overrideTitle = variantId ? this.variantTitleMap?.[variantId] : null;
    this.productTitleEl.textContent = overrideTitle || this.defaultProductTitle;
  }

  updateCtaLabel() {
    if (!this.ctaText) return;
    this.ctaText.textContent = this.cartContext ? 'Add to bag' : 'Add to product';
  }

  closeCartDrawer() {
    if (this.cartContext && this.cartDrawer) {
      this.cartDrawer.close(true);
    }
  }

  openCartDrawer() {
    if (this.cartDrawer) {
      this.cartDrawer.open();
    }
  }

  getOverrideLabel(position, value) {
    if (!value || !this.optionLabelOverrides) return null;
    const map = this.optionLabelOverrides[position];
    if (!map) return null;
    return map[value] || null;
  }

  createMediaHtml(imageData) {
    if (!imageData?.url) return '';
    const widthAttr = imageData.width ? ` width="${imageData.width}"` : '';
    const heightAttr = imageData.height ? ` height="${imageData.height}"` : '';
    const altText = this.escapeHtml(imageData.alt || '');
    return `<img class="gift-note-drawer__image" src="${imageData.url}" alt="${altText}" loading="lazy"${widthAttr}${heightAttr}>`;
  }

  escapeHtml(str) {
    if (typeof str !== 'string') return '';
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return str.replace(/[&<>"']/g, (char) => map[char]);
  }
}

customElements.define('gift-note-drawer', GiftNoteDrawer);
